import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageCircle, Phone, ChevronRight, Check } from "lucide-react";
import { fetchContent, waLink, telLink } from "@/lib/api";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import StickyBar from "@/components/landing/StickyBar";
import { Reveal } from "@/components/landing/Shared";

import { CATEGORY_PHOTOS } from "@/lib/photos";

const EXTRA_PHOTOS = CATEGORY_PHOTOS;

export default function CategoryPage() {
  const { key } = useParams();
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchContent()
      .then(setContent)
      .catch(() => setContent({ categories: [], settings: {}, locations: [] }));
    window.scrollTo(0, 0);
  }, [key]);

  const categories = content?.categories || [];
  const settings = content?.settings || {};
  const cat = categories.find((c) => c.key === key);

  useEffect(() => {
    if (cat) {
      document.title = `${cat.name} en San Pedro Sula | Electrodomésticos Rivera`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta)
        meta.setAttribute(
          "content",
          `Compra ${cat.name.toLowerCase()} importados en San Pedro Sula, Honduras. Garantía por escrito y financiamiento disponible. Cotiza por WhatsApp en Electrodomésticos Rivera.`
        );
    }
  }, [cat]);

  if (content && !cat) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-5 text-center">
        <h1 className="text-3xl font-extrabold tracking-tight text-primary" data-testid="category-not-found">
          Categoría no encontrada
        </h1>
        <Link to="/" data-testid="category-back-home" className="text-sm font-semibold text-whatsapp">
          Volver al inicio
        </Link>
      </div>
    );
  }

  if (!cat) return null;

  const photos = EXTRA_PHOTOS[cat.key] || [];
  const others = categories.filter((c) => c.key !== cat.key);

  return (
    <div className="pb-[60px] md:pb-0" data-testid="category-page">
      <Navbar settings={settings} />
      <main className="bg-white pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <nav className="flex items-center gap-2 text-xs font-semibold text-muted-foreground" data-testid="category-breadcrumb">
              <Link to="/" className="hover:text-primary">Inicio</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-primary">{cat.name}</span>
            </nav>
          </Reveal>
          <div className="mt-8 grid items-start gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <h1 className="text-4xl font-extrabold tracking-tighter text-primary sm:text-5xl" data-testid="category-title">
                  {cat.name} en San Pedro Sula
                </h1>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                  {cat.blurb} En Electrodomésticos Rivera encuentras {cat.name.toLowerCase()} importados de las mejores
                  marcas — Whirlpool, Maytag, Samsung, LG y más — con garantía por escrito y financiamiento disponible.
                  Cotiza por WhatsApp o visítanos en cualquiera de nuestras dos sucursales en San Pedro Sula.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {["Garantía por escrito", "Servicio técnico por 1 año", "Financiamiento disponible", "Productos importados", "Envíos a todo Honduras"].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm font-semibold text-primary">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-primary/20">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2} className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid="category-whatsapp-cta"
                  href={waLink(settings, `Hola Electrodomésticos Rivera, quiero cotizar: ${cat.name}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-whatsapp px-8 text-base font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-5 w-5" /> Cotizar {cat.name}
                </a>
                <a
                  data-testid="category-call-cta"
                  href={telLink(settings)}
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full border-2 border-primary px-8 text-base font-semibold text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
                >
                  <Phone className="h-5 w-5" /> Llamar
                </a>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              {cat.image && (
                <Reveal delay={0.1}>
                  <div className="relative">
                    <div className="absolute -left-4 -top-4 h-full w-full border-2 border-primary/15" aria-hidden="true" />
                    <img
                      src={cat.image}
                      alt={`${cat.name} en venta en San Pedro Sula — Electrodomésticos Rivera`}
                      className="relative aspect-[4/3] w-full object-cover"
                      data-testid="category-image"
                    />
                  </div>
                </Reveal>
              )}
            </div>
          </div>

          {photos.length > 0 && (
            <div className="mt-16 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4" data-testid="category-photos">
              {photos.map((p, i) => (
                <Reveal key={p.src} delay={Math.min(i * 0.05, 0.3)}>
                  <figure className="group relative overflow-hidden border border-border bg-white">
                    <img
                      src={p.src}
                      alt={`${p.label} — Electrodomésticos Rivera, San Pedro Sula`}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      data-testid={`category-photo-${i}`}
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 bg-primary/85 px-3 py-2 text-xs font-semibold text-white">
                      {p.label}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}

          <div className="mt-16 border-t border-border pb-20 pt-10">
            <h2 className="text-xl font-extrabold tracking-tight text-primary" data-testid="category-others-title">
              También te puede interesar
            </h2>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="category-others">
              {others.map((c) => (
                <Link
                  key={c.id || c.key}
                  to={`/${c.key}`}
                  data-testid={`category-other-${c.key}`}
                  className="border border-border px-4 py-2.5 text-sm font-semibold text-primary transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-white"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer settings={settings} locations={content?.locations || []} />
      <StickyBar settings={settings} />
    </div>
  );
}
